<template>
  <div class="update-banner bg-white rounded-lg shadow-sm p-6 mb-10">
    <header class="mb-8">
      <h1 class="text-xl font-bold text-neutral-700">
        {{ isEdit ? "Edit Banner" : "Buat Banner Baru" }}
      </h1>
    </header>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="space-y-6">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-2">
            Nama Banner
          </label>
          <TextField
            type="text"
            placeholder="Masukkan nama banner"
            v-model="form.name"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-2">
            Lokasi Banner
          </label>
          <CustomSelect
            :options="locations"
            :default="form.location"
            placeholder="Pilih lokasi banner"
            @input="onSelectLocation"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-2">
            Posisi
          </label>
          <TextField
            type="number"
            placeholder="Posisi tampil banner"
            v-model="form.position"
          />
        </div>
      </div>

      <div class="space-y-6">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-2">
            Status
          </label>
          <div class="flex gap-2">
            <span
              @click="form.isActive = true"
              :class="form.isActive ? activeClass : inactiveClass"
              class="px-4 py-1 rounded-full text-sm cursor-pointer transition"
            >
              Active
            </span>
            <span
              @click="form.isActive = false"
              :class="!form.isActive ? inactiveRedClass : inactiveClass"
              class="px-4 py-1 rounded-full text-sm cursor-pointer transition"
            >
              Inactive
            </span>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-2">
            Gambar Banner
          </label>
          <input type="file" @change="onFileChange" accept="image/*" />

          <div class="mt-3">
            <img
              v-if="preview"
              :src="preview"
              alt="Preview"
              class="max-h-40 rounded-lg"
            />
          </div>
        </div>
      </div>

      <div class="md:col-span-2 space-y-4">
        <label class="block text-sm font-medium text-neutral-700">
          Tampilkan Banner Di
        </label>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="border rounded-lg p-4 flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="font-medium text-neutral-700">Web</p>
                <p class="text-xs text-neutral-500">Banner tampil di website</p>
              </div>

              <button
                type="button"
                @click="form.isWeb = !form.isWeb"
                class="relative w-12 h-6 rounded-full transition-colors duration-200"
                :class="form.isWeb ? 'bg-green-500' : 'bg-gray-300'"
              >
                <span
                  class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transform transition-transform duration-200"
                  :class="form.isWeb ? 'translate-x-6' : 'translate-x-0'"
                />
              </button>
            </div>
            
            <div v-if="form.isWeb">
              <label class="block text-xs font-medium text-neutral-700 mb-1">Web Redirect URL</label>
              <TextField type="text" placeholder="Masukkan URL redirect web" v-model="form.webRedirectUrl" />
            </div>
          </div>

          <div class="border rounded-lg p-4 flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="font-medium text-neutral-700">Mobile</p>
                <p class="text-xs text-neutral-500">
                  Banner tampil di aplikasi mobile
                </p>
              </div>

              <button
                type="button"
                @click="form.isMobile = !form.isMobile"
                class="relative w-12 h-6 rounded-full transition-colors duration-200"
                :class="form.isMobile ? 'bg-green-500' : 'bg-gray-300'"
              >
                <span
                  class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transform transition-transform duration-200"
                  :class="form.isMobile ? 'translate-x-6' : 'translate-x-0'"
                />
              </button>
            </div>

            <div v-if="form.isMobile">
              <label class="block text-xs font-medium text-neutral-700 mb-1">Mobile Redirect URL</label>
              <TextField type="text" placeholder="Masukkan URL redirect mobile" v-model="form.mobileRedirectUrl" />
            </div>
          </div>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Badge Baru Sampai
        </label>
        <TextField type="datetime-local" v-model="form.badgeNewUntil" />
      </div>
    </div>

    <footer class="mt-8 flex justify-end gap-3">
      <button
        @click="$router.push('/banner')"
        class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
      >
        Batal
      </button>

      <button
        @click="submit"
        :disabled="loading"
        class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium disabled:opacity-50"
      >
        {{
          loading
            ? "Menyimpan..."
            : isEdit
            ? "Simpan Perubahan"
            : "Simpan Banner"
        }}
      </button>
    </footer>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import TextField from "@/components/Forms/TextField"
import CustomSelect from "@/components/Forms/CustomSelect"
import dayjs from "dayjs"

export default {
  name: "CreateBanner",
  components: { TextField, CustomSelect },

  data() {
    return {
      loading: false,
      preview: null,
      locations: ["SLIDER", "ANNOUNCEMENT"],
      form: {
        name: "",
        location: "",
        position: 1,
        image: null,
        badgeNewUntil: "",
        isActive: true,
        isWeb: true,
        webRedirectUrl: "",
        isMobile: true,
        mobileRedirectUrl: ""
      }
    }
  },

  computed: {
    ...mapGetters("banner", ["bannerDetail"]),
    isEdit() {
      return !!this.$route.params.id
    },

    activeClass() {
      return "bg-green-500 text-white"
    },
    inactiveClass() {
      return "bg-gray-200 text-gray-700"
    },
    inactiveRedClass() {
      return "bg-red-500 text-white"
    }
  },

  watch: {
    bannerDetail: {
      immediate: true,
      handler(val) {
        if (!this.isEdit || !val) return

        this.form.name = val.name || ""
        this.form.location = val.location || ""
        this.form.position = val.position || 1
        this.form.isActive = !!val.isActive
        this.form.isWeb = !!val.isWeb
        this.form.webRedirectUrl = val.webRedirectUrl || ""
        this.form.isMobile = !!val.isMobile
        this.form.mobileRedirectUrl = val.mobileRedirectUrl || ""

        if (val.badgetNewUntil) {
          this.form.badgeNewUntil = dayjs(val.badgetNewUntil).format(
            "YYYY-MM-DDTHH:mm"
          )
        }

        if (val.imageURL) {
          this.preview = val.imageURL
        }
      }
    }
  },

  methods: {
    ...mapActions("banner", [
      "createBanner",
      "updateBanner",
      "getBannerDetail"
    ]),

    onSelectLocation(val) {
      this.form.location = val
    },

    onFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      this.form.image = file
      this.preview = URL.createObjectURL(file)
    },

    async submit() {
      if (!this.form.name) return alert("Nama banner harus diisi")
      if (!this.form.location) return alert("Lokasi banner harus dipilih")
      if (!this.form.position || this.form.position < 1)
        return alert("Posisi tidak valid")
      if (!this.isEdit && !this.form.image)
        return alert("Gambar banner harus diupload")

      this.loading = true
      this.$store.dispatch("common/showLoading")

      try {
        const payload = new FormData()
        payload.append("name", this.form.name)
        payload.append("location", this.form.location)
        payload.append("position", this.form.position)
        payload.append("is_active", this.form.isActive)
        payload.append("is_web", this.form.isWeb)
        payload.append("web_redirect_url", this.form.webRedirectUrl || "")
        payload.append("is_mobile", this.form.isMobile)
        payload.append("mobile_redirect_url", this.form.mobileRedirectUrl || "")

        if (this.form.badgeNewUntil) {
          payload.append(
            "badge_new_until",
            dayjs(this.form.badgeNewUntil).format("YYYY-MM-DD HH:mm:ss")
          )
        }

        if (this.form.image) {
          payload.append("image", this.form.image)
        }

        if (this.isEdit) {
          await this.updateBanner({
            id: this.$route.params.id,
            payloads: payload
          })
          this.$toast.success("Banner berhasil diperbarui")
        } else {
          await this.createBanner({ payloads: payload })
          this.$toast.success("Banner berhasil dibuat")
        }

        this.$router.push("/banner")
      } catch (err) {
        console.error(err)
        this.$toast.error(
          err.response?.data?.message || "Gagal menyimpan banner"
        )
      } finally {
        this.loading = false
        this.$store.dispatch("common/hideLoading")
      }
    }
  },

  async mounted() {
    if (this.isEdit) {
      await this.getBannerDetail({ id: this.$route.params.id })
    }
  }
}
</script>
